import { useEffect, useMemo } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import axios from 'axios';
import { useLanguage, useNavigation, useProvider, useUser, addToDate } from '../../Hooks';
import { Route } from '~/Constants/Routes.constants';
import { ClaimManagerModalContainerProps } from '.';
import { useLocation } from 'react-router-dom';

const { PROVIDER_SELECT } = Route;

export const useClaimManagerModal = ({ onRightsGranted }: ClaimManagerModalContainerProps) => {
  // eslint-disable-next-line import/no-named-as-default-member
  const cancelRequests = axios.CancelToken.source();
  const { t } = useLanguage();
  const loc = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getParams, navigate } = useNavigation();

  const matchOrg = loc.pathname.startsWith(Route.ORGANISATION_ROOT);
  const { domain } = getParams();
  const { currentProvider, getCurrentProvider, isCurrentProviderLoading } = useProvider();
  const {
    currentUser,
    roleWithoutOrg: role,
    DANGEROUSLY_isSuperAdmin,
    DANGEROUSLY_isPartnergramSupportAgent,
    hasUserLoaded,
    hasRolesLoaded,
    updateRoles,
    roleUpdateResponse,
  } = useUser();

  const hasAccess = useMemo(
    () =>
      currentProvider?.uuid &&
      !!roleUpdateResponse?.providerId &&
      roleUpdateResponse?.providerId === currentProvider?.uuid,
    [roleUpdateResponse, currentProvider],
  );
  const gainedAccess = useMemo(() => hasAccess || !!role, [hasAccess, role]);

  useEffect(() => {
    if (
      currentProvider &&
      currentProvider.domain !== domain &&
      !matchOrg &&
      !isCurrentProviderLoading
    ) {
      getCurrentProvider(domain || '');
    }
  }, [currentProvider, isCurrentProviderLoading]);

  useEffect(() => {
    onRightsGranted(gainedAccess);

    return () => {
      cancelRequests.cancel();
    };
  }, [gainedAccess]);

  useEffect(() => {
    if (
      !role &&
      hasUserLoaded &&
      hasRolesLoaded &&
      !DANGEROUSLY_isSuperAdmin &&
      !DANGEROUSLY_isPartnergramSupportAgent
    ) {
      navigate(Route.PROVIDER_SELECT);
    }
  }, [
    role,
    hasUserLoaded,
    hasRolesLoaded,
    DANGEROUSLY_isSuperAdmin,
    DANGEROUSLY_isPartnergramSupportAgent,
  ]);

  // Handlers
  const onClaimRightsConfirm = async () => {
    const adminRightExpiryDate = addToDate(new Date().toISOString(), { hours: 24 });
    updateRoles({
      userId: currentUser?.uuid ?? '',
      providerId: currentProvider?.uuid ?? '',
      endDateTime: adminRightExpiryDate.toISOString(),
    });
  };

  const onClose = () => {
    navigate(PROVIDER_SELECT);
  };

  return {
    t,
    isMobile,
    hasAccess: gainedAccess,
    isLoading: !hasUserLoaded || !hasRolesLoaded,
    DANGEROUSLY_isSuperAdmin: DANGEROUSLY_isSuperAdmin || DANGEROUSLY_isPartnergramSupportAgent,
    onClaimRightsConfirm,
    onClose,
    providerName: currentProvider?.name || '',
  };
};
